import { useForm } from '@refinedev/react-hook-form';

import { BaseForm } from '@/components/form/form';
import FormField from '@/components/form/form-field';
import useLayout from '@/hooks/useLayout';
import { useSnackbar } from '@rrexpress/core';
import { type IClonedKitbirdDropdownOptionItem } from '@/modules/apps/model/kitbird/dropdown';

export type ContactFormProps = {
  type: 'update' | 'create',
  initialValue?: any,
  onFinish: (value: any) => void,
};

const ContactForm = ({ type, initialValue, onFinish }: ContactFormProps) => {
  useLayout({ title: 'Kontak' });
  const { show } = useSnackbar();

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    ...restUseFormInstance
  } = useForm();

  function handleOnFinish({
    village = {},
    city = {},
    created_time_utc,
    updated_time_utc,
    ...value
  }: any) {
    const payload = {
      ...value,
      contact_code: value.contact_code || '',
      email: value.email || '',
      phone_number: value.phone_number || '',
      address_village_name: village.record?.village_name || value.address_village_name,
      address_subdistrict_name: village.record?.subdistrict_name || value.address_subdistrict_name,
      address_zipcode: village.record?.zipcode || value.address_zipcode,
      address_country_id: city.record?.country_id || value.address_country_id,
      address_country_name: city.record?.country_name || value.address_country_name,
      address_province_id: city.record?.province_id || value.address_province_id,
      address_province_name: city.record?.province_name || value.address_province_name,
    };

    if (!payload?.address_subdistrict_name) {
      show('Kecamatan / kota tidak boleh kosong', 'error');
      return;
    }

    if (!payload?.address_village_name) {
      show('Kelurahan / kode pos tidak boleh kosong', 'error');
      return;
    }

    onFinish(payload);
  }

  return (
    <BaseForm
      initialValue={initialValue}
      onFinish={handleOnFinish}
      customForm={{
        handleSubmit,
        watch,
        setValue,
        getValues,
        ...restUseFormInstance,
      }}
    >
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            component="form"
            name="contact_name"
            label="Nama Kontak"
            componentProps={{
              label: 'Nama Kontak',
              required: true,
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            id="phone_number"
            component="form"
            name="phone_number"
            label="No Telp"
            componentProps={{
              label: 'No Telp',
              type: 'text',
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            id="email"
            component="form"
            name="email"
            label="Email"
            componentProps={{
              label: 'Email',
              type: 'text',
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            id="address_street"
            component="textarea"
            name="address_street"
            label="Alamat"
            componentProps={{
              label: 'Alamat',
              type: 'text',
              rows: 4,
              required: true,
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            id="address_city_name"
            component="queryfield"
            name="city"
            label="Kecamatan / Kota"
            value={type === 'update' ? `${initialValue?.address_subdistrict_name}, ${initialValue?.address_city_name}, ${initialValue?.address_province_name}, ${initialValue?.address_country_name}` : getValues('address_city_name')}
            componentProps={{
              endpoint: 'subdistricts',
              required: true,
              keyOptionValue: 'subdistrict_id',
              keyQueryFilter: 'subdistrict_name',
              keyOptionLabel: (record) => `${record?.subdistrict_name}, ${record?.city_name}, ${record?.province_name}, ${record?.country_name}`,
              dataProviderName: 'masterdata',
              onSelected: (record: any) => {
                setValue('address_subdistrict_id', (record as IClonedKitbirdDropdownOptionItem).value);
                setValue('address_subdistrict_name', (record as IClonedKitbirdDropdownOptionItem).label);
                setValue('address_city_name', record?.record?.city_name);
                setValue('address_city_id', record?.record?.city_id);
              },
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <div className="w-[600px]">
          <FormField
            id="address_village_name"
            component="queryfield"
            name="village"
            label="Kelurahan / Kode Pos"
            value={type === 'update' ? `${initialValue?.address_village_name || ''} - ${initialValue?.address_zipcode}` : getValues('address_village_name')}
            componentProps={{
              disabled: !getValues('address_subdistrict_id'),
              endpoint: 'villages',
              required: true,
              keyOptionValue: 'village_id',
              keyQueryFilter: 'village_name',
              queryFilter: {
                subdistrict_id: getValues('address_subdistrict_id'),
              },
              keyOptionLabel: (record) => `${record?.village_name} - ${record?.zipcode}`,
              dataProviderName: 'masterdata',
              onSelected: (record: any) => {
                setValue('address_village_id', (record as IClonedKitbirdDropdownOptionItem).value);
                setValue('address_village_name', (record as IClonedKitbirdDropdownOptionItem).label);
              },
            }}
          />
        </div>
      </div>
      <div className="mb-4 flex gap-3 items-center justify-between">
        <FormField
          id="is-active"
          component="checkbox"
          name="is_active"
          componentProps={{
            label: 'Aktif',
            checked: type === 'update' ? initialValue?.is_active : true,
          }}
        />
      </div>
    </BaseForm>
  );
};

export default ContactForm;
